import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { ProvisioningService } from '../../services/provisioning/provisioning.service';
import { ToastService } from '../../services/toast/toast.service';
import { PermissionService } from '../../services/permission/permission.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { ProvisionDialogComponent } from './provision-dialog/provision-dialog.component';
import { CancelDialogComponent } from './cancel-dialog/cancel-dialog.component';
import { ForwardDialogComponent } from './forward-dialog/forward-dialog.component';

@Component({
  selector: 'app-provision-queue',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './provision-queue.component.html',
  styleUrls: ['./provision-queue.component.scss', '../../../common-style/dropdown.scss'],
})
export class ProvisionQueueComponent {
  requisitionMode = new FormControl<string>('Online', { nonNullable: true });
  requisitionCategory = new FormControl<string>('Regular', { nonNullable: true });
  requisitionType = new FormControl<string>('Family', { nonNullable: true });
  requisitionFromDate = new FormControl('');

  modes = ['Online', 'Manual'];
  categories = ['Regular', 'Utsav'];
  // 'All' searches the whole queue in one go; locking still needs one type
  types = ['All', 'Individual', 'Family', 'Bulk'];

  results: any[] = [];
  hasSearched: boolean = false;
  isLoading: boolean = false;

  // client-side narrowing over the loaded rows: status tabs, keyword, paging
  statusTab: string = 'ALL';
  keyword: string = '';
  page: number = 1;
  readonly pageSize = 20;
  statusTabs = [
    { key: 'ALL', label: 'All' },
    { key: 'OPEN', label: 'Open' },
    { key: 'LOCKED', label: 'Locked' },
    { key: 'CONFIRMED', label: 'Confirmed' },
    { key: 'TOCANCEL', label: 'Sent for cancel' },
    { key: 'CANCELLED', label: 'Cancelled' },
  ];

  // the seven queue tiles (open regular/utsav rooms + dorms, admin queue,
  // pending cancellations, exception-eligible) for the working window
  counts: any = null;
  countsWindowDays: number = 7;

  canProvision: boolean = false;
  canRollback: boolean = false;
  canCancel: boolean = false;
  canForward: boolean = false;

  constructor(
    private provisioningService: ProvisioningService,
    private toastService: ToastService,
    private permissionService: PermissionService,
    private dialog: MatDialog
  ) {
    this.canProvision = this.permissionService.can('provision-queue', 'can_provision');
    this.canRollback = this.permissionService.can('provision-queue', 'can_rollback');
    this.canCancel = this.permissionService.can('provision-queue', 'can_cancel');
    this.canForward = this.permissionService.can('provision-queue', 'can_forward');
    this.loadCounts();
  }

  // best-effort tiles: a failure just hides them
  loadCounts(): void {
    this.provisioningService.getQueueCounts().subscribe({
      next: (res: any) => {
        this.counts = res?.data?.counts || null;
        this.countsWindowDays = res?.data?.windowDays || 7;
      },
      error: () => {
        this.counts = null;
      },
    });
  }

  get hasActions(): boolean {
    return this.canProvision || this.canRollback || this.canCancel || this.canForward;
  }

  private filters(): any {
    return {
      requisitionMode: this.requisitionMode.value,
      requisitionCategory: this.requisitionCategory.value,
      requisitionType: this.requisitionType.value === 'All' ? null : this.requisitionType.value,
      requisitionFromDate: this.requisitionFromDate.value || null,
    };
  }

  private matchesTab(row: any, key: string): boolean {
    const status = (row?.requisitionStatus || '').toUpperCase();
    const lc = (row?.requisitionLifecycleStatus || '').toUpperCase();
    switch (key) {
      case 'OPEN':
        return ['PENDING', 'URGENT', 'WAITLISTED', 'WAITLIST'].includes(status) && lc !== 'IN-PROGRESS';
      case 'LOCKED':
        return lc === 'IN-PROGRESS';
      case 'CONFIRMED':
        return status === 'CONFIRMED' && lc !== 'BOOKED_TOCANCEL';
      case 'TOCANCEL':
        return lc === 'BOOKED_TOCANCEL';
      case 'CANCELLED':
        return status === 'CANCELLED';
      default:
        return true;
    }
  }

  tabCount(key: string): number {
    return this.results.filter((r) => this.matchesTab(r, key)).length;
  }

  setTab(key: string): void {
    this.statusTab = key;
    this.page = 1;
  }

  onKeywordChange(): void {
    this.page = 1;
  }

  get filteredResults(): any[] {
    const q = this.keyword.trim().toLowerCase();
    return this.results.filter(
      (r) =>
        this.matchesTab(r, this.statusTab) &&
        (!q ||
          [r.requisitionId, r.familyCode, r.requisitionType, r.requisitionStatus]
            .some((v) => (v ?? '').toString().toLowerCase().includes(q)))
    );
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredResults.length / this.pageSize));
  }

  get pagedResults(): any[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredResults.slice(start, start + this.pageSize);
  }

  prevPage(): void {
    if (this.page > 1) this.page--;
  }

  nextPage(): void {
    if (this.page < this.totalPages) this.page++;
  }

  search(): void {
    this.isLoading = true;
    this.loadCounts();
    this.provisioningService.searchByQueue(this.filters()).subscribe({
      next: (res: any) => {
        this.results = res?.data?.requisitionDetails || [];
        this.hasSearched = true;
        this.page = 1;
        this.isLoading = false;
      },
      error: (err) => {
        this.results = [];
        this.hasSearched = true;
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load queue.');
      },
    });
  }

  // one button: free every aged lock in the working window (old-system parity)
  rollbackAll(): void {
    this.isLoading = true;
    this.provisioningService.rollbackBulkAll().subscribe({
      next: (res: any) => {
        const count = res?.data?.recordsUpdated ?? 0;
        this.toastService.success(`${res?.message || 'Done.'} (${count} lock${count === 1 ? '' : 's'})`);
        this.search();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to roll back the aged locks.');
      },
    });
  }

  lockOldest(): void {
    if (!this.requisitionFromDate.value) {
      this.toastService.error('Select a from-date to lock the oldest requisition.');
      return;
    }
    if (this.requisitionType.value === 'All') {
      this.toastService.error('Select a specific type to lock the oldest requisition.');
      return;
    }
    this.isLoading = true;
    this.provisioningService.lockOldest(this.filters()).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Oldest requisition locked.');
        this.search();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to lock oldest requisition.');
      },
    });
  }

  provision(row: any): void {
    this.openThenRefresh(
      ProvisionDialogComponent,
      { bookingId: row.requisitionId, category: row.requisitionCategory },
      '800px'
    );
  }

  cancel(row: any): void {
    this.openThenRefresh(CancelDialogComponent, { bookingId: row.requisitionId });
  }

  forward(row: any): void {
    this.openThenRefresh(ForwardDialogComponent, { bookingId: row.requisitionId });
  }

  // send a CONFIRMED booking to the admin queue for cancellation
  forwardCancel(row: any): void {
    this.openThenRefresh(ForwardDialogComponent, { bookingId: row.requisitionId, cancelMode: true });
  }

  // -- status-aware row actions: show only what applies to the row ---------
  // open requests can be provisioned / cancelled / forwarded; a CONFIRMED
  // booking can only be sent to the admin for cancellation; a locked row
  // (IN-PROGRESS) can be rolled back; a row already sent shows a note.
  private isOpen(row: any): boolean {
    // the same "open" set the backend uses (URGENT counts; both waitlist spellings exist)
    return ['PENDING', 'URGENT', 'WAITLISTED', 'WAITLIST'].includes(row?.requisitionStatus);
  }

  showProvision(row: any): boolean {
    return this.canProvision && this.isOpen(row);
  }

  showRollback(row: any): boolean {
    return this.canRollback && row?.requisitionLifecycleStatus === 'IN-PROGRESS';
  }

  showCancel(row: any): boolean {
    return this.canCancel && this.isOpen(row);
  }

  showForward(row: any): boolean {
    return this.canForward && this.isOpen(row);
  }

  showForwardCancel(row: any): boolean {
    return (
      this.canForward &&
      row?.requisitionStatus === 'CONFIRMED' &&
      row?.requisitionLifecycleStatus !== 'BOOKED_TOCANCEL'
    );
  }

  awaitingCancel(row: any): boolean {
    return row?.requisitionLifecycleStatus === 'BOOKED_TOCANCEL';
  }

  rollback(row: any): void {
    this.isLoading = true;
    this.provisioningService.rollback(row.requisitionId, false).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Lock rolled back.');
        this.search();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Rollback failed.');
      },
    });
  }

  private openThenRefresh(component: any, data: any, width = '500px'): void {
    const ref = this.dialog.open(component, { width, maxWidth: '95vw', data });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.search();
    });
  }
}
